import { CollectionViewer, DataSource, SelectionChange } from '@angular/cdk/collections';
import { BehaviorSubject, merge, Observable, of, Subscription } from 'rxjs';
import { first, map } from 'rxjs/operators';
import { SharepointObjectEntity } from '../../../../dave-data-module/entities/sharepoint-object.entity';
import { DynamicFolderTreeFlatNode } from '../../../dynamic-folder-tree-data.service';
import { DynamicFolderTreeDataSourceInterface } from '../folder-tree-datasource';
import { SharepointDataService } from './sharepoint-data.service';
import { SharepointFolderTreeControl } from './sharepoint-folder-tree-control';

export class SharepointFolderTreeDataSource implements DataSource<DynamicFolderTreeFlatNode<string>>, DynamicFolderTreeDataSourceInterface<string> {
    dataChange = new BehaviorSubject<DynamicFolderTreeFlatNode<string>[]>([]);
    private childrenSubscriptions = new Map<string, Subscription>();
    private expansionSub?: Subscription;

    get data(): DynamicFolderTreeFlatNode<string>[] {
        return this.dataChange.value;
    }
    set data(value: DynamicFolderTreeFlatNode<string>[]) {
        this._treeControl.dataNodes = value;
        this.dataChange.next(value);
    }
    private _disabledFolderIds: string[] = [];
    set disabledFolderIds(ids: string[]) {
        this._disabledFolderIds = ids.slice();
        // this.data = this.data.map(d => d.disabled = d.item.isFolder ? this._disabledFolderIds.includes(d.item.entityId) : d.disabled)
        this.data.filter((d) => d.item.isFolder).forEach((d) => (d.disabled = ids.includes(d.item.entityId)));
        this.data = this.data;
    }
    get disabledFolderIds() {
        return this._disabledFolderIds;
    }
    constructor(private _treeControl: SharepointFolderTreeControl, private _database: SharepointDataService, rootId: string, private nameOverrides?: Map<string, string>) {
        this._database
            .get$({ Id: rootId })
            .pipe(first())
            .subscribe((root) => {
                this.data = [sharepointObjectToDynaFolderTreeFlatNode(root, 0, nameOverrides)];
            });
    }

    connect(collectionViewer?: CollectionViewer): Observable<DynamicFolderTreeFlatNode<string>[]> {
        this.expansionSub = this._treeControl.expansionModel.changed.subscribe((change) => {
            if ((change as SelectionChange<DynamicFolderTreeFlatNode<string>>).added || (change as SelectionChange<DynamicFolderTreeFlatNode<string>>).removed) {
                this.handleTreeControl(change as SelectionChange<DynamicFolderTreeFlatNode<string>>);
            }
        });

        return merge(collectionViewer?.viewChange || of(), this.dataChange).pipe(
            map(() => {
                return this.data;
            }),
        );
    }

    disconnect(collectionViewer: CollectionViewer): void {
        this.childrenSubscriptions.forEach((s) => {
            s.unsubscribe();
        });
        this.childrenSubscriptions.clear();

        this.expansionSub?.unsubscribe();
        this.dataChange.complete();
    }

    /** Handle expand/collapse behaviors */
    handleTreeControl(change: SelectionChange<DynamicFolderTreeFlatNode<string>>) {
        if (change.added) {
            change.added.forEach((node) => this.toggleNode(node, true));
        }
        if (change.removed) {
            change.removed
                .slice()
                .reverse()
                .forEach((node) => this.toggleNode(node, false));
        }
    }

    /**
     * Toggle the node, remove from display list
     */
    toggleNode(node: DynamicFolderTreeFlatNode<string>, expand: boolean) {
        const index = this.data.indexOf(node);
        if (!node.expandable || index < 0 || (expand && node.isLoading)) {
            // If no children, or cannot find the node, no op
            return;
        }

        const notifyChange = () => {
            // notify the change
            node.isLoading = false;
            this.dataChange.next(this.data);
        };
        if (expand) {
            if (this.childrenSubscriptions.has(node.UId)) {
                console.error('subscription already running ' + node.UId);
            } else {
                node.isLoading = true;
                this.childrenSubscriptions.set(
                    node.UId,
                    this._database.getFolderByParent(node.item.entityId).subscribe((res) => {
                        const nodes = res.map((so) => sharepointObjectToDynaFolderTreeFlatNode(so, node.level + 1));
                        const parentIndex = this.data.indexOf(node);
                        if (parentIndex < 0) {
                            throw 'parentIndex not found';
                            return;
                        }
                        let lastChildIndex =
                            this.data.findIndex((e, index) => {
                                return index > parentIndex && e.level <= node.level;
                            }) - 1;
                        if (lastChildIndex < 0) {
                            lastChildIndex = this.data.length - 1;
                        }
                        // const startIndex = parentIndex + 1
                        const alreadyChildren = lastChildIndex !== parentIndex;
                        if (alreadyChildren) {
                            const nodesToAdd = [];
                            nodes.forEach((newNode) => {
                                const currentIndex = this.data.findIndex((d) => d.UId === newNode.UId);
                                if (currentIndex < 0) {
                                    nodesToAdd.push(newNode);
                                }
                            });
                            const nodesToDelete: number[] = [];
                            for (let i = parentIndex + 1; i < this.data.length && this.data[i].level > node.level; i++) {
                                if (!nodes.some((newNode) => newNode.UId === this.data[i].UId)) {
                                    this.childrenSubscriptions.get(this.data[i].UId)?.unsubscribe();
                                    this.childrenSubscriptions.delete(this.data[i].UId);
                                    nodesToDelete.push(i);
                                }
                            }
                            nodesToDelete.forEach((index) => {
                                this.data.splice(index, 1);
                            });
                            this.data.splice(parentIndex + 1, 0, ...nodesToAdd);
                        } else {
                            this.data.splice(parentIndex + 1, 0, ...nodes);
                        }
                        if (nodes.length === 0) {
                            node.expandable = false;
                        }
                        notifyChange();
                        this._treeControl.expanded.next(node);
                    }),
                );
            }
            // firstValueFrom(this._database.getChildren(node)).then((res) => {
            //     const nodes = res
            //     this.data.splice(index + 1, 0, ...nodes);
            //     if (nodes.length === 0) {
            //         node.expandable = false;
            //     }
            //     notifyChange();
            //     this._treeControl.expanded.next(node)
            // });
        } else {
            this.childrenSubscriptions.get(node.UId)?.unsubscribe();
            this.childrenSubscriptions.delete(node.UId);
            let count = 0;
            for (let i = index + 1; i < this.data.length && this.data[i].level > node.level; i++, count++) {}
            if (count) {
                this.data.splice(index + 1, count);
            }
            notifyChange();
        }
    }
    path$(leafId: string) {
        return this._database.getFolderAndParentsById(leafId).pipe(map((folders) => folders.map(f => ({Id: f.Id, Name: this.nameOverrides?.has(f.Id) ? this.nameOverrides.get(f.Id) : f.Name})).reverse()));
    }
}
function sharepointObjectToDynaFolderTreeFlatNode(entity: SharepointObjectEntity, level: number, nameOverrides?: Map<string, string>): DynamicFolderTreeFlatNode<string> {
    const name = nameOverrides?.has(entity.Id) ? nameOverrides.get(entity.Id) : entity.Name
    return new DynamicFolderTreeFlatNode<string>(
        {
            name$: of(name),
            isFolder: entity.IsFolder,
            entityId: entity.Id,
            type: 'sharepoint',
        },
        level,
        entity.IsFolder,
        false,
        false,
    );
}
